import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Sparkles, BookOpenCheck, Coins, GraduationCap, Award, Users, ArrowRight, Lock, ShieldAlert, Heart } from "lucide-react";
import SpeakerButton from "../components/SpeakerButton";
import MicButton from "../components/MicButton";
import QuestTracker from "../components/QuestTracker";
import MissionBoard from "../components/MissionBoard";
import StationTelemetry from "../components/StationTelemetry";
import LoreFeed from "../components/LoreFeed";
import AssessmentModal from "../components/AssessmentModal";
import ReadingChart from "../components/ReadingChart";
import MissionCelebration from "../components/MissionCelebration";
import Navigation from "../components/Navigation";
import BadgeDisplay from "../components/BadgeDisplay";
import RankUpCinematic from "../components/RankUpCinematic";
import StreakBadge from "../components/StreakBadge";
import NextLessonChip from "../components/NextLessonChip";
import {
  fetchCadet, fetchMissions, completeMission, deleteMission,
  fetchAssessmentHistory, fetchCurriculum, fetchMentorMessages, fetchStreak, tickStreak
} from "../lib/api";

const BG = "https://static.prod-images.emergentagent.com/jobs/476fc817-b76a-4f2c-9d9d-7d25b61f6972/images/b1eee161d34c19df351979fc5e3123d8fa732372fb8027c7023a52221f9ecc07.png";

export const Bridge = () => {
  const [cadet, setCadet] = useState(null);
  const [missions, setMissions] = useState([]);
  const [showAssess, setShowAssess] = useState(false);
  const [history, setHistory] = useState([]);
  const [celebrate, setCelebrate] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [mentorMessages, setMentorMessages] = useState([]);
  const [streak, setStreak] = useState({ streak_days: 0, last_active_date: null });
  const [rankUp, setRankUp] = useState(null);
  const [prevRank, setPrevRank] = useState(null);

  const refreshCadet = async () => setCadet(await fetchCadet());
  const refreshMissions = async () => setMissions(await fetchMissions());
  const refreshHistory = async () => setHistory(await fetchAssessmentHistory().catch(() => []));
  const refreshCurriculum = async () => setCurriculum(await fetchCurriculum().catch(() => null));
  const refreshMentorMessages = async () => setMentorMessages(await fetchMentorMessages().catch(() => []));
  const refreshStreak = async () => setStreak(await fetchStreak().catch(() => ({ streak_days: 0 })));

  useEffect(() => {
    (async () => {
      const c = await fetchCadet();
      setCadet(c);
      setPrevRank(c.rank);
      if (!c.onboarded) setShowAssess(true);
      await refreshMissions();
      await refreshHistory();
      await refreshCurriculum();
      await refreshMentorMessages();
      await refreshStreak();
      // tick streak on first visit each session/day
      tickStreak().then(setStreak).catch(() => {});
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect rank-up
  useEffect(() => {
    if (cadet?.rank && prevRank && cadet.rank !== prevRank) {
      const order = ["Cadet", "Pilot", "Commander", "Navigator"];
      if (order.indexOf(cadet.rank) > order.indexOf(prevRank)) {
        setRankUp(cadet.rank);
      }
      setPrevRank(cadet.rank);
    }
  }, [cadet?.rank, prevRank]);

  const onComplete = async (id) => {
    const r = await completeMission(id);
    if (r.cadet) setCadet(r.cadet);
    if (r.mission) setCelebrate(r.mission);
    await refreshMissions();
  };

  const onDelete = async (id) => {
    await deleteMission(id);
    await refreshMissions();
  };

  const onMissionAdded = (m) => setMissions([m, ...missions]);
  const onMissionsCreated = (ms) => setMissions([...ms, ...missions]);

  return (
    <div className="min-h-screen eigoo-stage-bg relative overflow-x-hidden">
      {/* Hero background image overlay */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black pointer-events-none" />

      <div className="relative">
        <Navigation cadet={cadet} streakDays={streak.streak_days} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <header className="flex items-center justify-end mb-6 gap-3 flex-wrap" data-testid="header">
          <StreakBadge days={streak.streak_days} testid="bridge-streak-badge" showBonus={true} />
          <button
            onClick={() => setShowAssess(true)}
            className="btn-eigoo btn-ghost-gold"
            data-testid="open-assessment"
          >
            <BookOpenCheck size={18} /> Reading Drill
          </button>
          <SpeakerButton
            text={`Welcome aboard, ${cadet?.callsign || "Commander"}. This is your CyberKids Academy bridge. Speak your vision into the microphone, complete your missions, and own the future.`}
            testid="speaker-welcome"
            size="lg"
          />
        </header>

        {/* Hero / Slogan */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mb-10 md:mb-14"
        >
          <div className="flex items-center gap-2 text-yellow-300 font-display font-bold tracking-[0.3em] text-xs mb-3">
            <Sparkles size={14} /> AGENTIC SIGNAL · SOVEREIGN BUSINESS
          </div>
          <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.92]">
            <span className="block">OWN THE</span>
            <span className="block bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-600 bg-clip-text text-transparent">FUTURE.</span>
            <span className="block text-red-500">Stat.</span>
          </h1>
          <div className="mt-4 flex items-center gap-2 text-white/70 text-base md:text-lg max-w-2xl">
            <p>Speak your <span className="text-yellow-300 font-bold">vision</span>. We'll <span className="text-yellow-300 font-bold">refine</span> it into <span className="text-red-400 font-bold">missions</span>.</p>
            <SpeakerButton text={"Speak your vision. We will refine it into missions. Own the future, Commander."} testid="speaker-hero" size="sm" />
          </div>
        </motion.section>

        {/* Quest Tracker */}
        <section className="mb-6">
          <NextLessonChip refreshKey={cadet?.xp || 0} />
        </section>
        <section className="mb-6">
          <Link
            to="/sentinel"
            data-testid="bridge-sentinel-teaser"
            className="block group rounded-2xl border border-red-400/30 bg-gradient-to-r from-red-950/60 via-black to-cyan-950/40 p-5 md:p-6 hover:border-red-400/60 transition relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
              background: "radial-gradient(500px 200px at 0% 50%, rgba(239,68,68,0.25), transparent 60%), radial-gradient(500px 200px at 100% 50%, rgba(34,211,238,0.18), transparent 60%)"
            }} />
            <div className="relative flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-red-500/15 border border-red-400/40 flex items-center justify-center">
                  <ShieldAlert className="text-red-300" size={22} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-red-300/80 font-display">Sovereign Sentinel · NEW</div>
                  <div className="font-display font-black text-xl text-white">Bug Bounty Vault is open.</div>
                  <div className="text-sm text-white/65">Hunt the flaw · earn the tribute · 70/20/10 split feeds CyberKids.</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-yellow-300 font-display font-black text-sm">
                Enter Vault <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
              </div>
            </div>
          </Link>
        </section>
        <section className="mb-8">
          <QuestTracker cadet={cadet} />
        </section>

        {/* Voice + Telemetry side by side */}
        <section className="grid lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2 eigoo-glass rounded-3xl p-6 md:p-8" data-testid="voice-panel">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center eigoo-glow-red">
                <Radio className="text-white" size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-red-300/80 font-display">Agentic Signal Refiner</div>
                <div className="font-display font-bold text-2xl md:text-3xl">Voice-to-Signal</div>
              </div>
              <SpeakerButton
                text="Tap the big microphone and tell me what you want to build or learn today. I will turn your words into missions."
                testid="speaker-voice-panel"
                size="sm"
              />
            </div>
            <MicButton
              onMissionsCreated={onMissionsCreated}
              onTranscript={() => {}}
            />
          </div>
          <div className="lg:col-span-1">
            <StationTelemetry />
          </div>
        </section>

        {/* Missions */}
        <section className="mb-12">
          <MissionBoard
            missions={missions}
            onComplete={onComplete}
            onDelete={onDelete}
            onMissionAdded={onMissionAdded}
          />
        </section>

        {/* Lore + Reading Progress */}
        <section className="grid lg:grid-cols-3 gap-6 mb-12">
          <div className="lg:col-span-2">
            <LoreFeed />
          </div>
          <div className="eigoo-glass rounded-3xl p-6" data-testid="reading-progress">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 rounded-2xl bg-white/5 border border-yellow-300/30 flex items-center justify-center">
                <BookOpenCheck className="text-yellow-300" size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-yellow-300/80 font-display">Reading Telemetry</div>
                <div className="font-display font-bold text-2xl">Power Words</div>
              </div>
              <SpeakerButton
                text={`You are at reading level ${cadet?.reading_level?.toFixed?.(1) || "1.0"}. You have mastered ${(cadet?.vocab_mastered||[]).length} power words.`}
                testid="speaker-reading"
                size="sm"
              />
            </div>
            <div className="rounded-2xl bg-black/40 border border-yellow-300/20 p-5 mb-4">
              <div className="text-xs uppercase tracking-widest text-white/50 font-display mb-1">Current Level</div>
              <div className="font-display font-black text-5xl text-yellow-200">{cadet?.reading_level?.toFixed?.(1) || "1.0"}</div>
              <div className="text-xs text-white/60 mt-1">grade level placement</div>
            </div>
            <div className="text-xs uppercase tracking-widest text-white/50 font-display mb-2">Mastered ({(cadet?.vocab_mastered||[]).length})</div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {(cadet?.vocab_mastered||[]).slice(0, 30).map(w => (
                <span key={w} className="px-2 py-0.5 rounded-md bg-green-500/10 text-green-200 text-sm border border-green-500/25">{w}</span>
              ))}
              {(cadet?.vocab_mastered||[]).length === 0 && (
                <span className="text-white/40 text-sm">Take a Reading Drill to begin tracking.</span>
              )}
            </div>
          </div>
        </section>

        {/* Reading Chart (full width) */}
        <section className="mb-8">
          <ReadingChart cadet={cadet} />
        </section>

        {/* Signal Science Badge Wall + Mentor Teaser */}
        <section className="grid lg:grid-cols-3 gap-6 mb-12" data-testid="academy-teaser">
          <div className="lg:col-span-2 eigoo-glass rounded-3xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center eigoo-glow-gold">
                  <GraduationCap className="text-black" size={22} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-yellow-300/80 font-display">Signal Science · Proof of Skill</div>
                  <div className="font-display font-bold text-2xl md:text-3xl">The Hall of Badges</div>
                </div>
                <SpeakerButton
                  text={`You have earned ${curriculum?.badges?.length || 0} Proof of Skill badges out of 4. Visit the Academy to earn more.`}
                  testid="speaker-hall-teaser"
                  size="sm"
                />
              </div>
              <Link to="/academy" className="btn-eigoo btn-gold" data-testid="goto-academy">
                Enter Academy <ArrowRight size={16} />
              </Link>
            </div>
            <div className="flex items-center justify-center gap-4 md:gap-8 flex-wrap">
              {(curriculum?.modules || []).map((m) => {
                const earned = !!(curriculum?.badges || []).find(b => b.module_id === m.id);
                return (
                  <div key={m.id} className="flex flex-col items-center gap-2" data-testid={`bridge-badge-${m.id}`}>
                    <BadgeDisplay
                      badge={{ ...m, module_id: m.id, module_numeral: m.numeral }}
                      size="md"
                      earned={earned}
                    />
                    <div className="text-[10px] uppercase tracking-widest mt-2 text-center max-w-[110px] leading-tight">
                      {earned ? <span className="text-green-300">Earned</span> : <span className="text-white/40 flex items-center justify-center gap-1"><Lock size={10}/>Module {m.numeral}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="eigoo-glass rounded-3xl p-6" data-testid="mentor-teaser">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 rounded-2xl bg-white/5 border border-red-500/30 flex items-center justify-center">
                <Users className="text-red-400" size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-red-300/80 font-display">Mentor Bridge</div>
                <div className="font-display font-bold text-xl">Privateer Signal</div>
              </div>
              <SpeakerButton
                text={`You have ${mentorMessages.filter(m=>!m.read).length} unread Privateer signals. Tap to enter the Mentor Bridge.`}
                testid="speaker-mentor-teaser"
                size="sm"
              />
            </div>
            {mentorMessages.length === 0 ? (
              <div className="text-sm text-white/60 mb-4">
                No signals yet. Open the Mentor Bridge to send your first signal to a Privateer.
              </div>
            ) : (
              <div className="space-y-2 mb-4">
                {mentorMessages.slice(0, 2).map(msg => (
                  <div key={msg.id} className="rounded-xl bg-black/40 border border-yellow-300/10 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="h-7 w-7 rounded-md bg-gradient-to-br from-yellow-300 to-yellow-600 text-black flex items-center justify-center font-display font-black text-sm">
                        {msg.mentor_avatar || msg.mentor_name?.[0]}
                      </div>
                      <div className="text-xs font-display font-bold truncate">{msg.mentor_name}</div>
                      {!msg.read && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40">new</span>}
                    </div>
                    <div className="text-xs text-white/70 line-clamp-2">{msg.body}</div>
                  </div>
                ))}
              </div>
            )}
            <Link to="/mentors" className="btn-eigoo btn-ghost-gold w-full" data-testid="goto-mentors">
              Open Mentor Bridge <ArrowRight size={16} />
            </Link>
          </div>
        </section>

        <footer className="text-center text-white/40 text-xs uppercase tracking-[0.3em] font-display py-6 border-t border-yellow-300/10">
          <div className="flex items-center justify-center gap-2">
            <Coins size={14} className="text-yellow-300/70" />
            CYBERKIDS ACADEMY · SOVEREIGN BRIDGE · 2026
          </div>
        </footer>
      </div>

      <AssessmentModal
        open={showAssess}
        onClose={() => { setShowAssess(false); refreshCadet(); refreshHistory(); }}
        onComplete={() => { refreshCadet(); refreshHistory(); }}
      />
      <MissionCelebration mission={celebrate} onDone={() => setCelebrate(null)} />
      <RankUpCinematic rank={rankUp} onDone={() => setRankUp(null)} />
    </div>
  );
};

export default Bridge;
